import { useEffect, useState, type JSX } from 'react';
import { z } from 'zod';
import { PillButton } from '@/components/entity';
import { Form, FormMessage, TextInputField } from '@/components/form';
import { useFormController } from '@/lib/forms/formController';
import { subscribeToAvailabilityAlertsPublic } from '@/lib/services/availabilityAlerts';
import { mergeContentWithDefaults } from '@/lib/utils/contentDefaults';
import type { AvailabilityAlertsContent } from '@/lib/types/siteSettings';

const SIGNUP_SCHEMA = z.object({
  email: z.string().trim().email('Enter a valid email'),
});

type SignupValues = z.infer<typeof SIGNUP_SCHEMA>;

const EMPTY_VALUES: SignupValues = { email: '' };

const DEFAULT_ALERTS_CONTENT: AvailabilityAlertsContent = {
  eyebrow: 'Availability Alerts',
  title: 'Get updates when units change status',
  description: 'Receive an email when a unit becomes available or unavailable.',
  emailLabel: 'Email address',
  emailPlaceholder: 'you@example.com',
  submitLabel: 'Subscribe',
  successMessage: "You're subscribed to availability alerts.",
  errorMessage: 'Unable to subscribe right now.',
  note: 'Note: Email delivery is queued but not yet enabled. This will start sending once the email provider integration is configured.',
};

export function AvailabilityAlertsSignup({
  content,
  id,
}: {
  content?: AvailabilityAlertsContent;
  id?: string;
}): JSX.Element {
  const resolvedContent = mergeContentWithDefaults(content, DEFAULT_ALERTS_CONTENT);
  const [showSuccess, setShowSuccess] = useState(false);

  const form = useFormController<SignupValues>({
    initialValues: EMPTY_VALUES,
    schema: SIGNUP_SCHEMA,
    onSubmit: async (values) => {
      await subscribeToAvailabilityAlertsPublic({ email: values.email });
    },
    mapSubmitError: (error) => ({
      message:
        resolvedContent.errorMessage.trim().length > 0
          ? resolvedContent.errorMessage
          : error instanceof Error
            ? error.message
            : 'Unable to subscribe right now.',
    }),
  });

  useEffect(() => {
    if (form.isDirty) {
      setShowSuccess(false);
    }
  }, [form.isDirty]);

  const emailField = form.field('email');

  return (
    <section
      id={id}
      className="rounded-3xl border border-surface-subtle bg-gradient-to-br from-brand-primary/5 via-white to-brand-accent/5 p-6 shadow-card md:p-8"
      aria-label="Availability alerts signup"
    >
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold uppercase tracking-wide text-brand-primary">
            {resolvedContent.eyebrow}
          </p>
          <h2 className="text-2xl font-semibold text-text-primary">{resolvedContent.title}</h2>
          <p className="max-w-2xl text-sm text-text-primary/70">{resolvedContent.description}</p>
        </div>

        <Form
          gap="sm"
          className="w-full max-w-xl"
          onSubmit={async (event) => {
            const succeeded = await form.handleSubmit(event);
            if (succeeded) {
              setShowSuccess(true);
              form.reset(EMPTY_VALUES);
            }
          }}
        >
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
            <TextInputField
              id="availability-alerts-email"
              label={resolvedContent.emailLabel}
              required
              type="email"
              value={emailField.value}
              onChange={emailField.onChange}
              onBlur={emailField.onBlur}
              error={form.touched.has('email') ? form.errors.email : undefined}
              placeholder={resolvedContent.emailPlaceholder}
              autoComplete="email"
              inputClassName="bg-white"
            />

            <div className="md:pb-[2px]">
              <PillButton type="submit" disabled={form.isSubmitting}>
                {resolvedContent.submitLabel}
              </PillButton>
            </div>
          </div>

          {showSuccess ? (
            <FormMessage tone="success" role="status">
              {resolvedContent.successMessage}
            </FormMessage>
          ) : null}

          {typeof form.submitError === 'string' && form.submitError.length > 0 ? (
            <FormMessage tone="error" role="alert">
              {form.submitError}
            </FormMessage>
          ) : null}
        </Form>
      </div>

      <p className="mt-5 text-xs text-text-primary/60">{resolvedContent.note}</p>
    </section>
  );
}
